import { SocialPlatforms } from "../components/campaign/types";
import { MLSFeatures } from "../components/listings/types";
import { AllListings } from "./allListings";
import { Images } from "./listingImages";
import { ListingVideos } from "./listingVideos";

// MB-1314: why a listing can't get an auto-generated slideshow video. Mirrors the API's
// AutoVideoSkipReasonCode minus NON_MLS. The named codes (property type, new construction,
// manufactured/mobile) drive a specific message; LISTING_TIER drives the generic fallback.
// `propertyType` carries the human-readable type label, set only for PROPERTY_TYPE.
export interface AutoVideoUnavailableReason {
    code:
        | "PROPERTY_TYPE"
        | "NEW_CONSTRUCTION"
        | "MANUFACTURED_HOME"
        | "LISTING_TIER";
    propertyType?: string;
}

// Mirrors marabot-api PropertyGroupPayload (property-group-payload.ts) (MB-1520).
export interface PropertyGroupSibling {
    listingId: string;
    // Required on the api; optional here so the UI tolerates an api that predates the marker.
    isPrimary?: boolean;
    mlsId?: string;
    saleType: "sale" | "rent";
    propertyType?: string;
    price?: number;
}
export interface PropertyGroupPayload {
    kind: "crosslist" | "salerent";
    isPrimary: boolean;
    marketSeparately: boolean;
    siblings: PropertyGroupSibling[];
}

export interface Features {
    architectureType: string;
    cooling: boolean;
    coolingType?: string;
    exteriorType: string;
    fireplace: boolean;
    fireplaceType?: string;
    floorCount: number;
    foundationType: string;
    garage: boolean;
    garageSpaces?: number;
    garageType?: string;
    heating: boolean;
    heatingType?: string;
    pool: boolean;
    poolType?: string;
    roofType: string;
    roomCount: number;
    unitCount: number;
    viewType?: string;
}

export interface ComfirmListing {
    _id: string;
    longitude: number;
    latitude: number;
    formattedAddress: string;
    propertyType: string;
    bedrooms: string;
    fullBathrooms: number;
    halfBathrooms: number;
    squareFootage: string;
    lotSize: string;
    yearBuilt: string;
    currentPrice?: number;
    legalDescription: string;
    features: Features | MLSFeatures;
    footer: {
        agentName: string;
        agentPhone: string;
        agentAgency: string;
        agencyPhone: string;
        sourceMLS: string;
        sourceMLSId: string;
        sharedAgentName?: string;
        sharedAgentAgency?: string;
        sharedAgentPhone?: string;
    };
    // This listing's MLS agent identity (Trestle-mirrored). Present only on MLS
    // previews; used by the add-listing agent-confirmation flow to compare against
    // the user's saved MLS details (first-capture / match / mismatch).
    agent?: {
        listAgentMlsId: string;
        listAgentFullName: string;
        listOfficeName: string;
        listAOR: string;
    };
    status?: string;
    mlsStatus?: string;
    lastSaleDate?: string;
    mlsPropertyType?: string;
    mlsPropertySubType?: string;
    mlsStructureType?: string;
    exists?: boolean;
    // MB-1472: commercial listing fields. zoning prefers the Trestle description over the raw
    // code, undefined when the feed has neither. leaseAmount/leaseAmountFrequency are the raw
    // Trestle values; leaseMonthlyDisplay is non-null ONLY when the api safely classified an
    // explicit monthly lease amount ($100-$50,000, frequency = Monthly) - never invent a
    // suffix from the raw fields. marketingCategory is present only for
    // commercial/investor/agricultural listings (e.g. "office", "business-for-sale").
    zoning?: string;
    leaseAmount?: number;
    leaseAmountFrequency?: string;
    leaseMonthlyDisplay?: number | null;
    marketingCategory?: string;
    pricingOverride?: "show" | "hide" | null;
    // MB-1472 follow-up: the account's commercial pricing preference already
    // resolved by the api (per-listing override > account setting). Present
    // only for commercial listings; residential omits it, so callers keep
    // their existing `?? true`.
    commercialUsePriceDefault?: boolean;
}

export interface UserListing extends ComfirmListing, AllListings {
    // MLS flag: false when the seller withheld the address (no street, no map).
    InternetAddressDisplayYN?: boolean;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
    county?: string;
    overlayUrl: string;
    droneVideo?: string;
    listingUrl?: string;
    images?: Images[];
    videos?: ListingVideos[];
    additionalContent?: string;
    userNotes?: string;
    listingType: ListingType;
    campaignPlatforms: SocialPlatforms[];
    // MB-1314: photos that qualify for auto-video (mirrors the API's
    // campaignQualifiedImageCount). MLS-only. Drives the listing-header note.
    campaignQualifiedImageCount?: number;
    // MB-1314: whether this listing's campaign has auto-video generation turned on —
    // the qualified-photos note only shows once it is on. MLS-only.
    autoGenerateSlideshow?: boolean;
    // MB-1314: when auto-video is on but the listing is ineligible by policy, the reason
    // for the card's "not available" note. Absent when auto-video is off or eligible.
    autoVideoUnavailableReason?: AutoVideoUnavailableReason;
    // MB-1490: server-resolved recency claim (under 7 days on market, not a
    // relist). Absent on an older API response must read as badge ON — mirrors
    // the video2 compositor's absent=>ON back-compat (template_1.py).
    recentlyListed?: boolean;
    // MB-1520: the record's MLS number; propertyGroup / campaignPauseReason come
    // from AllListings. Absent on an older API response.
    mlsId?: string;
    schoolDistrict?: string;
    sharedListing?: boolean;
    isSyncing?: boolean;
    campaigns?: {
        openHouseMonitoring?: boolean;
    }
}

export enum ListingType {
    MLSListing = "MLSListing",
    RentcastListing = "RentcastListing",
    Promotion = "Promotion",
    TextOnly = "textonly",
    // My Niche Video: a client-side clone of My Brand, captioned flow only
    NicheVideo = "nichevideo",
}
